import { useCallback, useEffect, useRef, useState } from "react";

export const HorizontalScrollHandler = ({
    containerRef,
    theme,
  }: {
    containerRef: React.RefObject<HTMLDivElement>;
    theme: "light" | "dark";
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [maxScroll, setMaxScroll] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const trackRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);
  
    useEffect(() => {
      if (!containerRef.current) return;
      
      const updateScrollInfo = () => {
        const container = containerRef.current;
        if (!container) return;
        
        setScreenWidth(window.innerWidth);
        
        if (window.innerWidth <= 1200) {
          setIsVisible(false);
          return;
        }
        
        const maxScrollValue = container.scrollWidth - container.clientWidth;
        setMaxScroll(maxScrollValue);
        
        const newScrollPosition = maxScrollValue > 0 
          ? (container.scrollLeft / maxScrollValue) * 100 
          : 0;
        
        setScrollPosition(newScrollPosition);
        
        if (maxScrollValue <= 1) { 
          setIsVisible(false);
        } else {
          checkParentScroll();
        }
      };
      
      const checkParentScroll = () => {
        if (window.innerWidth <= 1200) {
          setIsVisible(false);
          return;
        }
        
        const contentWrapper = document.querySelector('.content-section-wrapper');
        const studyPlannerWrapper = document.querySelector('.study-planner-wrapper');
        
        if (contentWrapper) {
          const scrolledToBottom = 
            contentWrapper.scrollHeight - contentWrapper.scrollTop <= contentWrapper.clientHeight + 10; // 10px buffer
          
          if (scrolledToBottom) {
            setIsVisible(false);
          } else if (containerRef.current && containerRef.current.scrollWidth > containerRef.current.clientWidth) {
            setIsVisible(true);
          }
        } else if (studyPlannerWrapper) {
          const scrolledToBottom = window.innerHeight + window.scrollY >= studyPlannerWrapper.scrollHeight - 100;
          
          if (scrolledToBottom) {
            setIsVisible(false);
          } else if (containerRef.current && containerRef.current.scrollWidth > containerRef.current.clientWidth) {
            setIsVisible(true);
          }
        }
      };
      
      containerRef.current.addEventListener('scroll', updateScrollInfo);
      
      updateScrollInfo();
      
      document.addEventListener('scroll', checkParentScroll, true);
      
      const handleResize = () => {
        updateScrollInfo();
        checkParentScroll();
      };
      
      window.addEventListener('resize', handleResize);
      
      window.addEventListener('load', handleResize);
      
      const intervalId = setInterval(handleResize, 500);
      setTimeout(() => clearInterval(intervalId), 5000);
      
      return () => {
        containerRef.current?.removeEventListener('scroll', updateScrollInfo);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('load', handleResize);
        document.removeEventListener('scroll', checkParentScroll, true);
        clearInterval(intervalId);
      };
    }, [containerRef]);
  
    const handleMouseDown = (e: React.MouseEvent) => {
      if (!containerRef.current || !trackRef.current) return;
      
      setIsDragging(true);
      setStartX(e.clientX);
      setScrollLeft(containerRef.current.scrollLeft);
      document.body.style.cursor = "grabbing";
    };
  
    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = "default";
    };
  
    const handleTrackClick = (e: React.MouseEvent) => {
      if (!containerRef.current || !trackRef.current || isDragging) return;
      
      const trackRect = trackRef.current.getBoundingClientRect();
      const clickPositionRatio = (e.clientX - trackRect.left) / trackRect.width;
      
      const scrollTo = maxScroll * clickPositionRatio;
      containerRef.current.scrollLeft = scrollTo;
    };
  
    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isDragging || !containerRef.current || !trackRef.current) return;
        
        e.preventDefault();
        
        const trackRect = trackRef.current.getBoundingClientRect();
        const delta = e.clientX - startX;
        const deltaPct = delta / trackRect.width;
        
        containerRef.current.scrollLeft = scrollLeft + (deltaPct * maxScroll);
      },
      [isDragging, startX, scrollLeft, containerRef, maxScroll]
    );
  
    const handleTouchStart = (e: React.TouchEvent) => {
      if (!containerRef.current) return;
      
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      setScrollLeft(containerRef.current.scrollLeft);
    };
  
    const handleTouchMove = useCallback(
      (e: TouchEvent) => {
        if (!isDragging || !containerRef.current || !trackRef.current) return;
        
        const trackRect = trackRef.current.getBoundingClientRect();
        const delta = e.touches[0].clientX - startX;
        const deltaPct = delta / trackRect.width;
        
        containerRef.current.scrollLeft = scrollLeft + (deltaPct * maxScroll);
      },
      [isDragging, startX, scrollLeft, containerRef, maxScroll]
    );
  
    const handleTouchEnd = () => {
      setIsDragging(false);
    };
  
    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("touchmove", handleTouchMove, {
          passive: false,
        });
        document.addEventListener("touchend", handleTouchEnd);
      } else {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      }
      
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        document.body.style.cursor = "default";
      };
    }, [isDragging, handleMouseMove, handleTouchMove]);
  
    if (screenWidth <= 1200 || !isVisible) return null;
  
    return (
      <div className={`scroll-handler-container ${theme}`}>
        <div 
          ref={trackRef} 
          className="scroll-track"
          onClick={handleTrackClick}
        >
          <div 
            ref={handleRef}
            className="scroll-handle"
            style={{ left: `${scrollPosition}%` }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          />
        </div>
      </div>
    );
  };